<template>
  <div class="spush-page">
    <div class="spush-page__wrap">
      <div class="spush-page__header">
        <div class="spush-page__header-logo"></div>
        <div class="spush-page__header-locale">
          <LocaleSelector />
        </div>
      </div>
      <div v-if="templateData.has_return_url" class="spush-page__control">
        <BackButton @click="redirectReturnUrl" />
      </div>
      <div
        :class="{ 'spush-page__body--min': !templateData.has_return_url }"
        class="spush-page__body">
        <p class="spush-page__body-title">{{ $t('spush_page__amount') }}</p>
        <div class="spush-page__body-info">
          <div v-if="templateData.symbol" class="spush-page__body-info__symbol">
            {{ templateData.symbol }}
          </div>
          <div class="spush-page__body-info__amount">{{ templateData.amount }}</div>
          <div v-if="!templateData.symbol" class="spush-page__body-info__currency">
            {{ templateData.currency }}
          </div>
        </div>
        <template v-if="templateData.deep_link">
          <div id="spushQR" class="spush-page__body-qr"></div>
          <p class="spush-page__body-scan-info">{{ $t('spush_page__info') }}</p>
        </template>
      </div>
      <div v-if="templateData.deep_link" class="spush-page__footer">
        <p class="spush-page__footer-title">{{ $t('spush_page__footer') }}</p>
        <DeeplinkButton :link="templateData.deep_link" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { MoneyFormatter } from '@/helpers/Formatting/MoneyFormatter';
import QRCodeStyling from 'qr-code-styling';
import BackButton from './components/BackButton.vue';
import DeeplinkButton from './components/DeeplinkButton.vue';
import FaviconIcon from './assets/favicon.svg';
import LocaleSelector from './components/LocaleSelector.vue';
import SpushPageFavicon from '@/assets/components/spuch-page-favicon.png';
import { setFavicon } from '@/helpers/setFavicon';
import { setPageTitle } from '@/helpers/setPageTitle';

export default {
  components: { BackButton, DeeplinkButton, LocaleSelector },
  created() {
    this.$i18n.locale = 'en';
    setFavicon(SpushPageFavicon);
    setPageTitle('Spush Pay');
  },
  mounted() {
    if (this.templateData.deep_link) {
      this.createQrCode();
    }
  },
  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    templateData() {
      const { has_return_url, id } = this.paymentInvoice;

      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        requisites,
        amount: payloadAmount,
        currency: payloadCurrency,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }

        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }

        return requisites;
      };

      const { deep_link } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;

      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        amount: this.formatAmount(amount, currency),
        symbol: this.currencySymbol(currency),
        currency,
        has_return_url,
        deep_link,
      };
    },
  },
  methods: {
    formatAmount(amount, currency) {
      const formatter = new MoneyFormatter();
      return formatter.format(amount, currency);
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'UAH':
          return '₴';
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
        case 'RUB':
          return '₽';
        default:
          return null;
      }
    },

    redirectReturnUrl() {
      if (!this.templateData.has_return_url) {
        return;
      }

      location.replace(`/return?id=${this.paymentInvoice.id}&force=true`);
    },

    createQrCode() {
      this.qrCode = new QRCodeStyling({
        width: 232,
        height: 232,
        type: 'svg',
        data: this.templateData.deep_link,
        image: FaviconIcon,
        backgroundOptions: {
          color: '#f9c2b7',
        },
        dotsOptions: {
          color: '#23063C',
          type: 'dots',
        },
        cornersSquareOptions: {
          type: 'extra-rounded',
          color: '#23063C',
        },
        cornersDotOptions: {
          type: 'dot',
          color: '#23063C',
        },
        imageOptions: {
          imageSize: 0.3,
          margin: 1,
        },
      });

      this.qrCode.append(document.getElementById('spushQR'));
    },
  },
};
</script>

<style lang="scss">
.spush-page {
  --spush-main-color: #23063c;
  --spush-second-color: #f9c2b7;

  --spush-form-color: #ffffff;
  --spush-form-shadow-color: rgba(117, 108, 104, 0.1);
  --spush-text-color: #767289;
  --spush-border-color: #f0f7fd;

  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 100;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Thin.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 100;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Thin.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 200;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-ExtraLight.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 200;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-ExtraLight.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 300;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Light.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 300;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Light.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Regular.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Regular.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Medium.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Medium.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Bold.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-Bold.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-ExtraBold.woff2') format('woff2');
    unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Outfit';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url('./assets/webfonts/Outfit-ExtraBold.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
      U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
      U+FFFD;
  }

  font-family: 'Outfit', sans-serif;
  background: transparent;

  &__wrap {
    max-width: 460px;
    margin: 0 auto;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 30px;
    margin-bottom: 40px;

    &-logo {
      width: 120px;
      height: 41px;
      background-image: url('./assets/logo.svg');
      background-repeat: no-repeat;
    }
  }

  &__control,
  &__body,
  &__footer {
    padding: 24px 40px;
    background: var(--spush-form-color);
    box-shadow: 0px 15px 26px 0px var(--spush-form-shadow-color);

    @media (max-width: 375px) {
      padding: 24px;
    }

    @media (max-width: 320px) {
      padding: 18px;
    }
  }

  &__control {
    padding: 12px 24px;
    border-radius: 24px 24px 0 0;
    border-bottom: 1px solid var(--spush-border-color);
  }

  &__body {
    margin-bottom: 8px;
    border-radius: 0 0 24px 24px;

    &--min {
      border-radius: 24px;
    }

    &-title {
      color: var(--spush-main-color);
      text-align: center;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin: 0 0 8px;
    }

    &-qr {
      width: fit-content;
      height: fit-content;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 20px;
      border-radius: 24px;
      margin: 0 auto;
      background: var(--spush-second-color);
    }

    &-scan-info {
      margin: 24px 0 0;
      font-size: 14px;
      text-align: center;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      color: var(--spush-text-color);
    }

    &-info {
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--spush-main-color);
      margin-bottom: 24px;
      gap: 8px;

      &__symbol {
        font-size: 26px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
      }

      &__amount {
        font-size: 36px;
        font-style: normal;
        font-weight: 600;
        line-height: 45px;
      }

      &__currency {
        font-size: 26px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }
    }
  }

  &__footer {
    border-radius: 24px;

    &-title {
      color: var(--spush-main-color);
      text-align: center;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 26px;
      margin: 0 0 20px;
    }
  }
}
</style>
